import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DocumentTextIcon, InformationCircleIcon, MagnifyingGlassIcon, RectangleStackIcon } from "@heroicons/react/24/outline";
import { Tooltip as InfoTooltip } from "react-tooltip";

import AgentLog from "../../../components/common/AgentLog";
import SelectPopover from "../../../components/common/SelectPopover";
import WeaveLoader from "../../../components/common/WeaveLoader";
import { sitePath } from "../../../utils/urls";
import {
  RUNNABLE_TYPES,
  SIDEBAR_TYPES,
  STATUS_META,
  ROW_HOVER,
  TYPE_LABELS,
  VITAL_META,
} from "./constants";
import {
  ChartTooltip,
  FilterChip,
  GradeDonut,
  PanelHeader,
  ReportSelector,
  ReportTypeIcon,
  RowChevron,
  StatusCounts,
  StatusDot,
  SideBlock,
  SideStack,
  TwoColumn,
  VitalsGauges,
} from "./shared";
import {
  demandEstimate,
  formatReportDate,
  formatTemplatePath,
  formatVital,
  formatVolume,
  tallyChecks,
  tallyChecksByCode,
  buildCheckIssue,
  vitalsFromChecks,
  VITAL_CODES,
  VITAL_SHORT,
} from "./utils";

export { FilterChip };

export const RunReportMenu = ({
  open,
  onToggle,
  creating,
  onCreate,
}) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onToggle();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onToggle]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={onToggle}
        disabled={creating}
        className="rounded-lg bg-theme-primary px-3 py-2 text-sm font-medium text-theme-button disabled:opacity-40"
      >
        {creating ? "Starting…" : "New Report"}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-theme-secondary bg-theme-main p-1 shadow-xl">
          {RUNNABLE_TYPES.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={item.disabled || creating}
              onClick={() => {
                if (item.disabled) return;
                onCreate(item.id);
              }}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                item.disabled
                  ? "cursor-not-allowed opacity-50"
                  : ROW_HOVER
              }`}
            >
              <span className="flex items-center gap-2 text-theme-text">
                <ReportTypeIcon type={item.id} />
                {item.label}
              </span>
              {item.note && (
                <span className="text-[11px] text-theme-secondary-text">{item.note}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const auditCheckCounts = (report) => ({
  fail: report?.headline?.fails ?? 0,
  warn: report?.headline?.warns ?? 0,
  pass: report?.headline?.passes ?? 0,
});

const ReportRowMetrics = ({ item }) => {
  if (item.status !== "succeeded") {
    const tagClass = {
      queued: "bg-theme-secondary text-theme-secondary-text",
      running: "bg-amber-500/15 text-amber-500",
      failed: "bg-red-500/15 text-red-500",
      canceled: "bg-theme-secondary text-theme-secondary-text",
    }[item.status];
    if (!tagClass) return null;
    return (
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${tagClass}`}>
        {item.status}
      </span>
    );
  }
  if (item.type === "page_audit" || item.type === "page_speed" || item.type === "ux_audit") {
    return <StatusCounts counts={auditCheckCounts(item)} />;
  }
  if (item.type === "demand") {
    const mid = item.headline?.value ?? item.usage?.estimate_mid;
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-theme-secondary-text">
        <MagnifyingGlassIcon className="h-3.5 w-3.5" />
        {formatVolume(mid)}/mo
      </span>
    );
  }
  return null;
};

const trendChartData = (trend) =>
  (trend || []).map((point, i) => ({
    ...point,
    // Unique X key — duplicate day labels break Recharts tooltip matching.
    x: point.hash || String(i),
    label: point.finished_at
      ? new Date(point.finished_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : "",
  }));

const trendTickFormatter = (data) => (x) =>
  data.find((point) => point.x === x)?.label || "";

const SidebarTrendChart = ({ type, trend = [] }) => {
  if (trend.length < 2) return null;
  const data = trendChartData(trend);

  if (type === "page_audit" || type === "ux_audit") {
    return (
      <div className="mt-3 h-28">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--theme-shadow)", opacity: 0.25 }} />
            <Bar dataKey="fail" stackId="a" fill={STATUS_META.fail.fill} name="Fail" />
            <Bar dataKey="warn" stackId="a" fill={STATUS_META.warn.fill} name="Warn" />
            <Bar dataKey="pass" stackId="a" fill={STATUS_META.pass.fill} name="Pass" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  if (type === "demand") {
    return (
      <div className="mt-3 h-28">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey="searches"
              name="Searches/mo"
              stroke="#6366f1"
              fill="#6366f133"
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return null;
};

const TYPE_FILTER_OPTIONS = [
  {
    id: "all",
    label: "All types",
    icon: <RectangleStackIcon className="h-3.5 w-3.5" />,
  },
  ...Object.keys(TYPE_LABELS).map((type) => ({
    id: type,
    label: TYPE_LABELS[type],
    icon: <ReportTypeIcon type={type} className="h-3.5 w-3.5" />,
  })),
];

export const ReportsList = ({
  reports,
  latest = {},
  loading,
  loadingMore,
  hasNext,
  onLoadMore,
  project,
  typeFilter,
  setTypeFilter,
  count,
  onCreate,
  creating,
}) => {
  const statsSide = (
    <SideStack>
      {SIDEBAR_TYPES.map((type) => {
        const report = latest[type];
        const demandMid = report
          ? (report.headline?.value ?? report.usage?.estimate_mid)
          : null;
        const runnable = RUNNABLE_TYPES.find((item) => item.id === type);
        const canStart = !!onCreate && !runnable?.disabled && !creating;
        return (
          <SideBlock
            key={type}
            title={TYPE_LABELS[type] || type}
            action={report ? (
              <Link
                to={sitePath(project, `seo/reports/${report.hash}/`)}
                className="shrink-0 text-xs font-normal text-theme-primary hover:underline"
              >
                view
              </Link>
            ) : null}
            meta={report ? (
              <>
                {(type === "page_audit" || type === "ux_audit" || type === "page_speed") && (
                  <StatusCounts counts={auditCheckCounts(report)} />
                )}
                {type === "demand" && (
                  <span className="inline-flex items-center gap-1 text-xs text-theme-secondary-text">
                    <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                    {formatVolume(demandMid)}/mo
                  </span>
                )}
              </>
            ) : null}
          >
            {!report ? (
              <p className="mt-2 text-sm text-theme-secondary-text">
                {runnable?.disabled ? (
                  runnable.note || "Coming soon"
                ) : (
                  <>
                    No report yet
                    {canStart && (
                      <>
                        {" · "}
                        <button
                          type="button"
                          onClick={() => onCreate(type)}
                          className="text-theme-primary hover:underline"
                        >
                          Start one
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
            ) : type === "page_speed" ? (
              <VitalsGauges vitals={report.usage?.vitals} />
            ) : (
              <SidebarTrendChart type={type} trend={report.trend || []} />
            )}
          </SideBlock>
        );
      })}
    </SideStack>
  );

  return (
    <TwoColumn
      side={statsSide}
      main={(
        <>
          <PanelHeader
            title="All Audits"
            count={count}
            filters={(
              <SelectPopover
                options={TYPE_FILTER_OPTIONS}
                value={typeFilter || "all"}
                onChange={setTypeFilter}
                placeholder="All types"
              />
            )}
          />
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <WeaveLoader size={64} />
            </div>
          ) : (
            <>
              <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
                {reports.map((item) => (
                  <li key={item.hash}>
                    <Link
                      to={sitePath(project, `seo/reports/${item.hash}/`)}
                      className={`flex items-center gap-3 px-3 py-2.5 ${ROW_HOVER}`}
                    >
                      <ReportTypeIcon type={item.type} className="h-6 w-6 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {TYPE_LABELS[item.type] || item.type} report
                          </span>
                          {item.is_latest && (
                            <span className="rounded-full bg-theme-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-theme-primary">
                              Latest
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-secondary-text">
                          <StatusDot status={item.status} />
                          <span className="truncate">
                            {item.create_date
                              ? new Date(item.create_date).toLocaleString()
                              : item.status}
                          </span>
                        </p>
                      </div>
                      <ReportRowMetrics item={item} />
                      <RowChevron />
                    </Link>
                  </li>
                ))}
                {!reports.length && (
                  <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                    No reports match this filter.
                  </li>
                )}
              </ul>
              {hasNext && (
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={onLoadMore}
                  className="mt-3 w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-medium text-theme-text seo-row-hover disabled:opacity-40"
                >
                  {loadingMore ? "Loading…" : "Next page"}
                </button>
              )}
            </>
          )}
        </>
      )}
    />
  );
};

const VitalsTrendCharts = ({ trend = [] }) => {
  if (trend.length < 2) {
    return (
      <p className="mt-4 text-xs text-theme-secondary-text">
        Run this report again to see trends.
      </p>
    );
  }
  const data = trendChartData(trend);
  const codes = VITAL_CODES.filter((code) => data.some((point) => point[code] != null));
  if (!codes.length) return null;
  return (
    <div className="mt-4 space-y-3 border-t border-theme-secondary pt-3">
      {codes.map((code) => (
        <VitalTrendRow key={code} code={code} data={data} />
      ))}
    </div>
  );
};

const VitalTrendRow = ({ code, data }) => {
  const tooltipId = useId();
  const meta = VITAL_META[code];
  const latest = [...data].reverse().find((point) => point[code] != null);
  const stroke = meta?.stroke || "#6366f1";
  return (
    <div>
      <div className="mb-1 flex items-center gap-1">
        <span className="text-xs font-medium text-theme-text">{VITAL_SHORT[code]}</span>
        {meta && (
          <>
            <a
              href={meta.docs}
              target="_blank"
              rel="noreferrer"
              data-tooltip-id={tooltipId}
              data-tooltip-content={meta.help}
              className="text-theme-secondary-text hover:text-theme-primary"
              aria-label={`About ${VITAL_SHORT[code]}`}
            >
              <InformationCircleIcon className="h-3.5 w-3.5" />
            </a>
            <InfoTooltip
              id={tooltipId}
              place="left"
              className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
            />
          </>
        )}
        <span className="ml-auto text-[11px] tabular-nums text-theme-secondary-text">
          {formatVital(code, latest?.[code])}
        </span>
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey={code}
              name={VITAL_SHORT[code]}
              stroke={stroke}
              fill={`${stroke}33`}
              strokeWidth={1.5}
              dot={false}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DEMAND_SCENARIOS = [
  { key: "low", name: "Low", stroke: "var(--theme-secondary-text)" },
  { key: "mid", name: "Estimate", stroke: "var(--theme-primary)" },
  { key: "high", name: "High", stroke: "var(--theme-secondary)" },
];

const DemandTrendChart = ({ data, tickFormatter }) => {
  const [only, setOnly] = useState(null);
  const gradientId = useId().replace(/:/g, "");
  const lines = DEMAND_SCENARIOS.filter((line) => data.some((point) => point[line.key] != null));
  const painted = [...lines].reverse();
  return (
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            {lines.map((line) => (
              <linearGradient
                key={line.key}
                id={`${gradientId}-${line.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={line.stroke} stopOpacity={0.28} />
                <stop offset="100%" stopColor={line.stroke} stopOpacity={0.03} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis dataKey="x" tickFormatter={tickFormatter} tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} width={36} tickFormatter={formatVolume} />
          <Tooltip content={<ChartTooltip />} />
          <Legend
            content={() => (
              <div className="mt-1 flex justify-center gap-3">
                {lines.map((line) => (
                  <button
                    key={line.key}
                    type="button"
                    onClick={() => setOnly((prev) => (prev === line.key ? null : line.key))}
                    className={`inline-flex items-center gap-1 text-[11px] ${
                      only && only !== line.key
                        ? "text-theme-secondary-text opacity-40"
                        : "text-theme-text"
                    }`}
                  >
                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: line.stroke }} />
                    {line.name}
                  </button>
                ))}
              </div>
            )}
          />
          {painted.map((line) => (
            <Area
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.name}
              stroke={line.stroke}
              fill={`url(#${gradientId}-${line.key})`}
              strokeWidth={line.key === "mid" ? 2 : 1.5}
              dot={false}
              hide={!!only && only !== line.key}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const TrendChart = ({ report }) => {
  const trend = report?.trend || [];
  if (trend.length < 2) {
    return (
      <div className="flex h-36 items-center justify-center rounded-xl border border-dashed border-theme-secondary text-xs text-theme-secondary-text">
        Run this report again to see trends.
      </div>
    );
  }
  const data = trendChartData(trend);
  const tickFormatter = trendTickFormatter(data);

  if (report.type === "demand") {
    return <DemandTrendChart data={data} tickFormatter={tickFormatter} />;
  }
  if (report.type === "page_audit" || report.type === "ux_audit") {
    return (
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="x" tickFormatter={tickFormatter} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={28} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="fail" stackId="a" fill={STATUS_META.fail.fill} name="Fail" />
            <Bar dataKey="warn" stackId="a" fill={STATUS_META.warn.fill} name="Warn" />
            <Bar dataKey="pass" stackId="a" fill={STATUS_META.pass.fill} name="Pass" />
            <Bar dataKey="skipped" stackId="a" fill={STATUS_META.skipped.fill} name="Skipped" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return null;
};

export const ReportDetail = ({ report, reports, project, onOpenScore, onOpenCheck, onSwitchReport }) => {
  const [issuesOnly, setIssuesOnly] = useState(true);

  useEffect(() => {
    setIssuesOnly(true);
  }, [report?.hash]);

  const auditCounts = useMemo(() => {
    if (!report || (report.type !== "page_audit" && report.type !== "page_speed" && report.type !== "ux_audit")) {
      return null;
    }
    const counts = { pass: 0, warn: 0, fail: 0, skipped: 0 };
    (report.scores || []).forEach((score) => {
      const t = tallyChecks(score.checks);
      counts.pass += t.pass;
      counts.warn += t.warn;
      counts.fail += t.fail;
      counts.skipped += t.skipped;
    });
    return counts;
  }, [report]);

  const sortedScores = useMemo(() => {
    const scores = report?.scores || [];
    return scores.slice().sort((a, b) => {
      const aT = tallyChecks(a.checks);
      const bT = tallyChecks(b.checks);
      return (bT.fail * 2 + bT.warn) - (aT.fail * 2 + aT.warn);
    });
  }, [report]);

  const checkRows = useMemo(() => {
    if (
      report?.type !== "page_audit"
      && report?.type !== "page_speed"
      && report?.type !== "ux_audit"
    ) {
      return [];
    }
    return tallyChecksByCode(report.scores);
  }, [report]);

  const showIssuesFilter = report?.type === "page_audit"
    || report?.type === "page_speed"
    || report?.type === "ux_audit";
  const visibleScores = useMemo(() => {
    if (!showIssuesFilter || !issuesOnly) return sortedScores;
    return sortedScores.filter((score) => {
      const counts = tallyChecks(score.checks);
      return counts.fail + counts.warn > 0;
    });
  }, [sortedScores, showIssuesFilter, issuesOnly]);
  const visibleChecks = useMemo(() => {
    if (!showIssuesFilter || !issuesOnly) return checkRows;
    return checkRows.filter((row) => (row.fail || 0) + (row.warn || 0) > 0);
  }, [checkRows, showIssuesFilter, issuesOnly]);

  if (!report) {
    return <p className="text-sm text-theme-secondary-text">Loading report…</p>;
  }
  const active = report.status === "queued" || report.status === "running";
  const sameType = (reports || []).filter((r) => r.type === report.type && r.status === "succeeded");
  const reportWhen = formatReportDate(report.finished_at || report.create_date);
  const typeLabel = TYPE_LABELS[report.type] || report.type;
  const detailTitle = reportWhen
    ? `${typeLabel} report from ${reportWhen}`
    : `${typeLabel} report`;

  const side = (
    <SideStack>
      {report.type === "page_speed" && (
        <SideBlock
          title="Core Web Vitals"
          meta={auditCounts ? <StatusCounts counts={auditCounts} /> : null}
        >
          <VitalsGauges vitals={report.usage?.vitals} />
          <VitalsTrendCharts trend={report.trend || []} />
        </SideBlock>
      )}
      {(report.type === "page_audit" || report.type === "ux_audit") && auditCounts && (
        <SideBlock
          title={report.type === "ux_audit" ? "UX check breakdown" : "Check breakdown"}
          meta={<StatusCounts counts={auditCounts} />}
        >
          <div className="mt-3">
            <GradeDonut
              counts={auditCounts}
              center={auditCounts.fail + auditCounts.warn}
              caption="issues"
            />
          </div>
        </SideBlock>
      )}
      {report.type === "demand" && (
        <SideBlock
          title="Estimated search demand"
          meta={(
            <span className="inline-flex items-center gap-1 text-xs text-theme-secondary-text">
              <MagnifyingGlassIcon className="h-3.5 w-3.5" />
              {formatVolume(report.headline?.value ?? report.usage?.estimate_mid)}/mo
            </span>
          )}
        />
      )}
      {report.type !== "page_speed" && (
        <SideBlock title="Over time">
          <div className="mt-3">
            <TrendChart report={report} />
          </div>
        </SideBlock>
      )}
    </SideStack>
  );

  return (
    <TwoColumn
      side={side}
      main={(
        <>
          <PanelHeader
            title={(
              <span className="inline-flex items-center gap-2">
                <Link
                  to={sitePath(project, "seo/reports/")}
                  className="text-theme-secondary-text hover:text-theme-primary"
                >
                  ←
                </Link>
                {detailTitle}
              </span>
            )}
            count={null}
            filters={(
              <>
                {showIssuesFilter && (
                  <>
                    <FilterChip active={issuesOnly} onClick={() => setIssuesOnly(true)}>
                      Issues only
                    </FilterChip>
                    <FilterChip active={!issuesOnly} onClick={() => setIssuesOnly(false)}>
                      All checks
                    </FilterChip>
                  </>
                )}
                <ReportSelector
                  reports={sameType}
                  value={report.hash}
                  onChange={onSwitchReport}
                  allowAllLatest={false}
                  typeFilter={report.type}
                />
              </>
            )}
          />
          {report.type !== "demand" && report.type !== "page_audit" && report.type !== "page_speed" && report.type !== "ux_audit" && (
            <p className="mb-4 -mt-2 text-sm text-theme-secondary-text">
              {report.headline?.display || report.summary || report.status}
            </p>
          )}

          <div className="space-y-4">
            {active && <AgentLog entries={report.agent_log || []} />}

            {report.type === "demand" && (
              <DemandBlock queries={report.queries || []} />
            )}

            {(report.type === "page_audit" || report.type === "page_speed" || report.type === "ux_audit") && (
              <div className="space-y-6">
                {checkRows.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-theme-secondary-text">
                      Checks
                    </h3>
                    <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
                      {visibleChecks.map((row) => (
                        <li key={row.code}>
                          <button
                            type="button"
                            onClick={() => {
                              const issue = buildCheckIssue(report, row.code);
                              if (issue && onOpenCheck) onOpenCheck(issue);
                            }}
                            className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${ROW_HOVER}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium">{row.label}</p>
                              {row.help && (
                                <p className="truncate text-xs text-theme-secondary-text">
                                  {row.help}
                                </p>
                              )}
                            </div>
                            <StatusCounts counts={row} />
                            <RowChevron />
                          </button>
                        </li>
                      ))}
                      {!visibleChecks.length && (
                        <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                          No checks with issues.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div>
                  <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-theme-secondary-text">
                    Pages
                  </h3>
                  <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
                    {visibleScores.map((score) => {
                      const issueLabels = (score.checks || [])
                        .filter((c) => c.status === "fail" || c.status === "warn")
                        .sort((a, b) => (a.status === "fail" ? 0 : 1) - (b.status === "fail" ? 0 : 1))
                        .map((c) => c.label || c.code)
                        .filter(Boolean);
                      const pageVitals = report.type === "page_speed"
                        ? vitalsFromChecks(score.checks)
                        : [];
                      return (
                      <li key={score.hash}>
                        <button
                          type="button"
                          onClick={() => onOpenScore(score)}
                          className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${ROW_HOVER}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">
                              {formatTemplatePath({ path: score.site_page_path })
                                || score.site_page_path
                                || score.site_page_route}
                            </p>
                            {pageVitals.length > 0 ? (
                              <p className="truncate text-xs text-theme-secondary-text">
                                {pageVitals
                                  .map((v) => `${VITAL_SHORT[v.code]} ${formatVital(v.code, v.value)}`)
                                  .join(" · ")}
                              </p>
                            ) : issueLabels.length > 0 ? (
                              <p className="truncate text-xs text-theme-secondary-text">
                                {issueLabels.join(" · ")}
                              </p>
                            ) : null}
                          </div>
                          <StatusCounts counts={tallyChecks(score.checks)} />
                          <RowChevron />
                        </button>
                      </li>
                      );
                    })}
                    {!visibleScores.length && (
                      <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                        {sortedScores.length
                          ? "No pages with issues."
                          : "No page scores on this report."}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {(report.suggestions || []).length > 0 && (
              <Link
                to={`${sitePath(project, "seo/suggestions/")}?report=${report.hash}`}
                className="inline-block text-sm text-theme-primary hover:opacity-80"
              >
                View {report.suggestions.length} suggestions →
              </Link>
            )}
          </div>
        </>
      )}
    />
  );
};

const COMPETITION_LABEL = { LOW: "Easy", MEDIUM: "Medium", HIGH: "High" };

const competitionDetail = (example) => {
  const raw = String(example.competition || "").toUpperCase();
  const label = COMPETITION_LABEL[raw] || example.competition;
  const index = example.competition_index;
  const score = index != null && index !== "" ? ` (${index} / 100)` : "";
  const bits = [`${label} Difficulty${score}`];
  if (example.cpc != null && example.cpc !== "") {
    bits.push(`$${Number(example.cpc).toFixed(2)} CPC`);
  }
  return bits.join(" · ");
};

const DemandBlock = ({ queries }) => {
  const tooltipId = useId();
  const sorted = [...(queries || [])].sort(
    (a, b) => (demandEstimate(b) || 0) - (demandEstimate(a) || 0),
  );
  if (!sorted.length) {
    return <p className="text-sm text-theme-secondary-text">No demand queries on this report.</p>;
  }
  return (
    <>
      <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
        {sorted.map((query) => {
          const mid = demandEstimate(query);
          const rawPath = query.site_page_path || query.metrics?.route || "";
          if (!rawPath) return null;
          const title = query.site_page_path
            ? formatTemplatePath({ path: query.site_page_path })
            : formatTemplatePath(rawPath);
          const population = query.metrics?.estimate?.population || 0;
          const examples = query.metrics?.examples || [];
          const low = query.metrics?.estimate?.low;
          const high = query.metrics?.estimate?.high;
          const range = low != null && high != null
            ? `${formatVolume(Math.min(low, high))} - ${formatVolume(Math.max(low, high))} estimated`
            : "";
          return (
            <li key={query.hash} className={`px-3 py-2.5 ${ROW_HOVER}`}>
              <div className="flex items-center gap-3">
                <p className="min-w-0 flex-1 truncate font-medium">{title}</p>
                <span
                  className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums text-theme-primary"
                  data-tooltip-id={range ? tooltipId : undefined}
                  data-tooltip-content={range || undefined}
                >
                  {formatVolume(mid)}
                  <span className="text-[10px] font-normal">/mo</span>
                </span>
                <span className="inline-flex w-14 shrink-0 items-center justify-end gap-1 text-xs tabular-nums text-theme-secondary-text">
                  {population > 1 && (
                    <>
                      <DocumentTextIcon className="h-3.5 w-3.5" />
                      {population}
                    </>
                  )}
                </span>
              </div>
              {examples.length > 0 && (
                <ul className="mt-2 space-y-1 border-t border-theme-secondary pt-2">
                  {examples.map((ex) => {
                    const level = COMPETITION_LABEL[String(ex.competition || "").toUpperCase()];
                    return (
                      <li key={ex.keyword} className="flex items-center gap-3 text-xs">
                        <span className="min-w-0 flex-1 truncate text-theme-secondary-text">
                          {ex.keyword}
                        </span>
                        <span className="w-16 shrink-0 text-right tabular-nums text-theme-text">
                          {ex.volume == null ? "—" : formatVolume(ex.volume)}
                        </span>
                        <span className="inline-flex w-14 shrink-0 justify-end">
                          {level && (
                            <span
                              data-tooltip-id={tooltipId}
                              data-tooltip-content={competitionDetail(ex)}
                              className={`cursor-default rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                level === "Easy"
                                  ? "bg-theme-primary/15 text-theme-primary"
                                  : level === "High"
                                    ? "border border-theme-text/30 text-theme-text"
                                    : "bg-theme-secondary text-theme-text"
                              }`}
                            >
                              {level}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <InfoTooltip id={tooltipId} place="left" className="z-50 !max-w-xs !text-xs" />
    </>
  );
};
